import { cn } from '../lib/cn';
import {
  Dialog as DialogRoot,
  DialogContent as DialogContentPrimitive,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './dialog';

/**
 * Modal (erp-design-guideline.md §6.5): Overlay abgedunkelt, Esc schließt,
 * X oben rechts, Footer rechtsbündig (Cancel links, Primary rechts).
 * Dünner Kompatibilitäts-Wrapper über den nova-Primitives (./dialog) —
 * behält die schlanke `title`/`footer`-API der bestehenden Aufrufer.
 * Dateiname „Modal", weil „Dialog.tsx" mit dem CLI-generierten dialog.tsx
 * kollidieren würde (case-insensitive Dateisysteme).
 */
export const Dialog = DialogRoot;
export { DialogTrigger };

export function DialogContent({
  className,
  title,
  description,
  children,
  footer,
  fullscreenOnMobile = false,
}: {
  className?: string;
  title: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /**
   * Unter `lg:` (1024px) füllt der Dialog den ganzen Viewport (Sheet) statt einer
   * kleinen zentrierten Box — für Formulare, die zu 90 % am iPad Mini (744 px
   * Breite im Hochformat) ausgefüllt werden. Über `lg:` unverändert die normale
   * zentrierte Box. Standardmäßig aus — jeder bestehende Dialog bleibt unverändert.
   */
  fullscreenOnMobile?: boolean;
}) {
  return (
    <DialogContentPrimitive
      className={cn(
        // `grid-cols-1` = eine Spalte `minmax(0,1fr)`: Ohne sie wächst die Grid-Spalte
        // mit der längsten nicht umbrechenden Zeile (z. B. `truncate` im Fuß), und der
        // ganze Dialog scrollt seitlich statt abzuschneiden.
        'grid-cols-1 gap-0 bg-background p-0 shadow-modal',
        fullscreenOnMobile
          ? [
              'fixed inset-0 top-0 left-0 h-[100dvh] w-screen max-w-none max-h-none translate-x-0 translate-y-0 flex flex-col rounded-none',
              'lg:inset-auto lg:top-1/2 lg:left-1/2 lg:h-auto lg:w-[calc(100vw-32px)] lg:max-w-[560px] lg:max-h-[calc(100dvh-2rem)] lg:-translate-x-1/2 lg:-translate-y-1/2 lg:rounded-xl lg:grid lg:flex-none',
            ]
          : 'w-[calc(100vw-32px)] max-w-[560px] sm:max-w-[560px]',
        className,
      )}
    >
      <DialogHeader className="gap-0.5 border-b p-4 pr-12 text-left">
        <DialogTitle className="text-md font-medium text-foreground">{title}</DialogTitle>
        {description && <DialogDescription>{description}</DialogDescription>}
      </DialogHeader>
      <div className={cn('overflow-y-auto p-6', fullscreenOnMobile ? 'flex-1 lg:max-h-[70vh] lg:flex-none' : 'max-h-[70vh]')}>
        {children}
      </div>
      {footer && (
        <DialogFooter className="mx-0 mb-0 gap-2 rounded-b-xl border-t bg-background p-4">
          {footer}
        </DialogFooter>
      )}
    </DialogContentPrimitive>
  );
}

/**
 * Große Dialog-Fläche OHNE vorgegebenes Innenleben — für flächige Arbeits-/
 * Auswahl-Dialoge, die Kopf, Spalten und Fuß selbst layouten und je Spalte
 * eigenständig scrollen (Artikel-Suche, ADR 0018-Design). `DialogContent` oben
 * schreibt Padding und `max-h-[70vh]` fest und passt dafür nicht.
 *
 * `title` bleibt Pflicht — Radix braucht für den Dialog eine Überschrift, sonst
 * warnt es und Screenreader kündigen den Dialog namenlos an. Rendert der Aufrufer
 * seine Überschrift selbst, blendet `visuallyHiddenTitle` sie nur optisch aus.
 */
export function DialogSurface({
  className,
  title,
  description,
  visuallyHiddenTitle = false,
  children,
}: {
  className?: string;
  title: string;
  description?: string;
  visuallyHiddenTitle?: boolean;
  children: React.ReactNode;
}) {
  return (
    <DialogContentPrimitive
      className={cn(
        // Die Defaults des Primitives bewusst neutralisieren: `grid`/`gap-4`/`p-4`
        // und vor allem `overflow-y-auto` — sonst scrollt der ganze Dialog statt
        // der einzelnen Spalten. `sm:max-w-none`, damit der Aufrufer die Breite setzt.
        'flex flex-col gap-0 overflow-hidden overflow-y-hidden bg-background p-0 shadow-modal sm:max-w-none',
        className,
      )}
    >
      <DialogHeader className={cn('text-left', visuallyHiddenTitle && 'sr-only')}>
        <DialogTitle className="text-md font-medium text-foreground">{title}</DialogTitle>
        {description && <DialogDescription>{description}</DialogDescription>}
      </DialogHeader>
      {children}
    </DialogContentPrimitive>
  );
}
